import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "neutral" | "ghost" | "danger";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-accent-ink border-transparent hover:opacity-90",
  secondary: "bg-surface text-ink border-line hover:bg-sunk",
  neutral: "bg-sunk text-ink border-transparent hover:bg-line-2",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-sunk",
  danger: "bg-transparent text-crit border-crit hover:bg-crit-soft",
};

const SIZES: Record<Size, string> = {
  sm: "text-meta px-2.5 py-1.5 gap-1.5",
  md: "text-ui px-3.5 py-2 gap-2",
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = "neutral",
  size = "md",
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-md border font-medium leading-tight",
        "transition-colors disabled:opacity-45 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
